import { getEventStatusDisplayLabel } from "../Bullion/lib/bullionPlayedMatchDetails";
import {
  EVENT_STATUS,
  cricketEventStatusAllowsInningsBreakInference,
  eventStatusToNumber,
  isBallInProgressOrMatchStable,
  isBallInProgressStatus,
  isBreakInPlayStatus,
} from "../Trader/Cricket/decimalEventStatus";
import { formatCountdownDuration, formatImminentCountdown, isImminentCountdownStatus } from "../../utils/matchCountdown";
import { MATCH_IMMINENT_MINUTES } from "../../utils/matchTimeHorizon";
import { Match } from "./MatchCards/types";
import { findMarketBySport, findMatchingRunner, getOddsFromRunner } from "./utils/marketUtils";

export { isImminentCountdownStatus };

export interface MatchStatus {
  text: string;
  color: string;
}

/**
 * Returns true if the given status string indicates a live/in-play stream or match.
 * Uses the same rules as getMatchStatus so streaming fixture "live" matches cricket match cards.
 * Use for streaming fixture status (e.g. "Match in Progress - Ball in Progress (est.latency:6 Seconds)").
 */
export const isStreamingFixtureLive = (status: string | undefined): boolean => {
  const s = (status || "").toUpperCase();
  if (
    s.includes("MATCH COMPLETE") ||
    s.includes("FINISHED") ||
    s.includes("FULL TIME") ||
    s.includes("EVENT CLOSED") ||
    s === "EVENT_FULL_RESULT"
  ) {
    return false;
  }
  return (
    s.includes("MATCH IN PROGRESS") ||
    s.includes("IN-PLAY") ||
    s.includes("IN PLAY") || // e.g. "Started In Play"
    s.includes("LIVE") ||
    s.includes("BREAK IN PLAY") ||
    s.includes("INNINGS COMPLETE") ||
    s.includes("PLAY TO RESTART") ||
    // Chase target ("Play to 150") — not pre-match "Play to Start Shortly"
    /\bPLAY TO \d/.test(s)
  );
};

/**
 * Returns true if cricket stream data indicates the match is in play (started).
 * Used so the match list shows "IN-PLAY" when the feed has updated even if the list API still has stale "scheduled".
 */
export const streamDataIndicatesCricketInPlay = (streamData: any): boolean => {
  if (!streamData) return false;
  const gp = streamData?.event?.scoreboard?.find((e: any) => e?.name === "game_phase")?.value;
  if (gp && isStreamingFixtureLive(gp)) return true;
  const es = streamData?.market_update?.event_status;
  if (es != null && isBreakInPlayStatus(es)) return true;
  if (
    es != null &&
    (es === 1 ||
      es === 2 ||
      es === "1" ||
      es === "2" ||
      String(es).toUpperCase() === "MATCH_STABLE" ||
      String(es).toUpperCase() === "BALL_IN_PROGRESS")
  )
    return true;
  const sc = streamData?.scorecard;
  if (sc && (Number(sc.inns_now) > 0 || (sc.inns1 && Number(sc.inns1.runs) > 0))) return true;
  return false;
};

const CRICKET_BREAK_IN_PLAY_COLOR = "text-[#a855f7]";

function cricketStreamScoreboard(streamData: any): any[] {
  const board = streamData?.event?.scoreboard ?? streamData?.event?.event?.scoreboard;
  return Array.isArray(board) ? board : [];
}

function cricketStreamScoreboardValues(streamData: any, names: string[]): unknown[] {
  const scoreboard = cricketStreamScoreboard(streamData);
  if (scoreboard.length === 0) return [];
  const values: unknown[] = [];
  for (const name of names) {
    const entry = scoreboard.find((e: any) => e?.name === name);
    const value = entry?.value;
    if (value != null && String(value).trim() !== "") values.push(value);
  }
  return values;
}

function cricketStreamBreakStatusSources(streamData: any): unknown[] {
  const eventStatus =
    streamData?.market_update?.event_status ??
    streamData?.event?.event_status ??
    streamData?.scorecard?.event_status;
  const scorecardEventStatus = streamData?.scorecard?.event_status;
  const scoreboardTexts = cricketStreamScoreboardValues(streamData, ["game_phase", "status"]);
  return [eventStatus, scorecardEventStatus, ...scoreboardTexts];
}

/** Rain / weather stoppage, including pre-toss "Toss Delayed due to bad weather". */
export function cricketFeedTextIndicatesWeatherDelay(raw: unknown): boolean {
  const upper = String(raw ?? "").toUpperCase();
  if (!upper.trim()) return false;
  return (
    upper.includes("RAIN DELAY") ||
    upper.includes("WET OUTFIELD") ||
    upper.includes("BAD WEATHER") ||
    upper.includes("WEATHER DELAY") ||
    upper.includes("TOSS DELAYED") ||
    /DELAYED DUE TO (BAD )?WEATHER/.test(upper) ||
    /COVERS? (ARE )?ON/.test(upper)
  );
}

function cricketFeedTextIndicatesBreakInPlay(raw: unknown): boolean {
  if (raw == null || raw === "") return false;
  if (isBreakInPlayStatus(raw)) return true;
  if (getEventStatusDisplayLabel(raw) === "BREAK IN PLAY") return true;
  const upper = String(raw).toUpperCase();
  return (
    upper.includes("BREAK IN PLAY") ||
    cricketFeedTextIndicatesWeatherDelay(raw) ||
    upper.includes("PLAY TO RESTART") ||
    /\bDRINKS\b/.test(upper)
  );
}

function cricketStreamScoreboardRow(streamData: any, name: string): string {
  const board = cricketStreamScoreboard(streamData);
  if (board.length === 0) return "";
  const value = board.find((e: any) => e?.name === name)?.value;
  return value == null ? "" : String(value).trim();
}

function cricketScoreboardCount(raw: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

export type CricketInningsBreakOpts = {
  /**
   * After the new innings has started (first BIP / ball), ignore Decimal sitting on
   * inns 2+ at 0.0 again — that flicker swaps the break panel with the bat/bowl strip.
   */
  dismissZeroOverInningsBreak?: boolean;
};

export type CricketInningsBreakLatch = {
  matchKey: string;
  inns: number;
  dismissed: boolean;
};

function cricketEventStatusIndicatesLiveDelivery(status: unknown): boolean {
  if (isBallInProgressStatus(status)) return true;
  const n = eventStatusToNumber(status);
  return (
    n === EVENT_STATUS.UNKNOWN_EVENT ||
    n === EVENT_STATUS.RUNS_LIKELY ||
    n === EVENT_STATUS.POSSIBLE_WICKET ||
    n === EVENT_STATUS.UMPIRE_REVIEW
  );
}

/**
 * Decimal is calling the match in play (stable, ball in progress, review, super over).
 * Scorecard / game_phase can keep an old "Rain Delay" or "Break in Play" line while
 * overs and commentary continue — Squares and Trader follow this status, not that copy.
 */
function cricketEventStatusIndicatesActivePlay(status: unknown): boolean {
  if (status == null || String(status).trim() === "") return false;
  if (isBreakInPlayStatus(status) || cricketFeedTextIndicatesBreakInPlay(status)) return false;
  if (isBallInProgressOrMatchStable(status) || cricketEventStatusIndicatesLiveDelivery(status)) return true;
  const n = eventStatusToNumber(status);
  if (n === EVENT_STATUS.SUPER_OVER || n === EVENT_STATUS.DATA_ASSEMBLY_ISSUE) return true;
  const upper = String(status).toUpperCase();
  return (
    upper.includes("MATCH IN PROGRESS") ||
    upper.includes("BALL IN PROGRESS") ||
    /\bIN[-\s]?PLAY\b/.test(upper)
  );
}

function cricketFeedTextIndicatesInningsRestartGap(raw: unknown): boolean {
  const upper = String(raw ?? "").toUpperCase();
  if (!upper.trim()) return false;
  return (
    upper.includes("PLAY TO RESTART") ||
    upper.includes("CHANGE OF INNINGS") ||
    upper.includes("INNINGS COMPLETE") ||
    upper.includes("INNINGS BREAK") ||
    /END OF (THE )?INNINGS/.test(upper)
  );
}

function cricketOverDeliveryHasBalls(overDelivery: unknown): boolean {
  return Array.isArray(overDelivery) && overDelivery.some((ball) => ball != null && String(ball).trim() !== "");
}

function cricketScoregridInnsRows(streamData: any, inns: number): unknown[] | null {
  const raw = streamData?.scoregrid;
  const wrapped = Array.isArray(raw) ? raw[0]?.msg_data ?? raw[0] : raw;
  const list = wrapped?.overs?.[`inns${inns}`] ?? wrapped?.scoregrid?.overs?.[`inns${inns}`];
  return Array.isArray(list) ? list : null;
}

function cricketScoregridInnsHasBall(streamData: any, inns: number): boolean {
  const list = cricketScoregridInnsRows(streamData, inns);
  if (!list) return false;
  for (const row of list) {
    for (let b = 1; b <= 12; b += 1) {
      const cell = (row as Record<string, unknown> | null)?.[`b${b}`];
      if (cell != null && String(cell).trim() !== "") return true;
    }
  }
  return false;
}

/** Highest scoregrid over index for an innings (e.g. 20 after a completed T20). */
function cricketScoregridInnsMaxOver(streamData: any, inns: number): number {
  const list = cricketScoregridInnsRows(streamData, inns);
  if (!list) return 0;
  let maxOv = 0;
  for (const row of list) {
    const ov = Math.floor(Number((row as { ov?: unknown } | null)?.ov));
    if (Number.isFinite(ov) && ov > maxOv) maxOv = ov;
  }
  return maxOv;
}

/** True when the current innings has actually started bowling — not just scoreboard inns 2 / 0.0. */
export function cricketNewInningsPlayIsUnderway(input: {
  eventStatus?: unknown;
  partOvers?: number;
  delv?: number;
  overDelivery?: unknown;
  completedOvers?: number;
}): boolean {
  if (cricketEventStatusIndicatesLiveDelivery(input.eventStatus)) return true;
  if ((input.partOvers ?? 0) > 0 || (input.delv ?? 0) > 0) return true;
  if ((input.completedOvers ?? 0) > 0) return true;
  return cricketOverDeliveryHasBalls(input.overDelivery);
}

export function cricketStreamCurrentInningsNumber(streamData: any): number {
  const fromBoard = cricketScoreboardCount(cricketStreamScoreboardRow(streamData, "Current Innings"));
  const fromSc = Math.floor(Number(streamData?.scorecard?.inns_now));
  const inns = Math.max(fromBoard, Number.isFinite(fromSc) && fromSc > 0 ? fromSc : 0);
  return inns >= 1 ? inns : 1;
}

export function cricketStreamIndicatesNewInningsPlayUnderway(streamData: any): boolean {
  if (!streamData) return false;
  const status = streamData?.market_update?.event_status ?? streamData?.event?.event_status;
  const part = cricketScoreboardCount(cricketStreamScoreboardRow(streamData, "Current Innings Part Overs"));
  const delv = cricketScoreboardCount(cricketStreamScoreboardRow(streamData, "current.delv"));
  const completedOvers = cricketScoreboardCount(cricketStreamScoreboardRow(streamData, "Current Innings Overs"));
  if (cricketNewInningsPlayIsUnderway({ eventStatus: status, partOvers: part, delv, completedOvers })) return true;
  const inns = cricketStreamCurrentInningsNumber(streamData);
  const innsBlock = streamData?.scorecard?.[`inns${inns}`];
  const batting = Array.isArray(innsBlock?.batting) ? innsBlock.batting : [];
  if (batting.some((b: { balls?: unknown }) => Number(b?.balls) > 0)) return true;
  return cricketScoregridInnsHasBall(streamData, inns);
}

export function advanceCricketInningsBreakLatch(
  prev: CricketInningsBreakLatch | null | undefined,
  next: { matchKey: string; inns: number; playUnderway: boolean },
): CricketInningsBreakLatch {
  if (!prev || prev.matchKey !== next.matchKey || prev.inns !== next.inns) {
    return { matchKey: next.matchKey, inns: next.inns, dismissed: Boolean(next.playUnderway) };
  }
  return {
    matchKey: prev.matchKey,
    inns: prev.inns,
    dismissed: prev.dismissed || Boolean(next.playUnderway),
  };
}

/**
 * Between innings with no break flag: Decimal sends BREAK_IN_PLAY ("Change of Innings") only briefly,
 * then drops back to MATCH_STABLE ("Play to Restart Shortly") or even MATCH_SCHEDULED (0)
 * until the next innings' first ball. Pretoss `curBall` (-3) must not hide that window.
 */
export function cricketStreamIndicatesInningsBreak(
  streamData: any,
  opts?: CricketInningsBreakOpts,
): boolean {
  if (!streamData) return false;
  const status = streamData?.market_update?.event_status ?? streamData?.event?.event_status;
  if (!cricketEventStatusAllowsInningsBreakInference(status)) return false;
  if (isBallInProgressStatus(status)) return false;

  const row = (name: string) => cricketStreamScoreboardRow(streamData, name);
  const winner = row("winner");
  if (winner && winner !== "NA") return false;

  const oversRaw = row("Current Innings Overs");
  if (oversRaw === "") return false;
  const inns = cricketScoreboardCount(row("Current Innings"));
  const overs = cricketScoreboardCount(oversRaw);
  const part = cricketScoreboardCount(row("Current Innings Part Overs"));
  const delv = cricketScoreboardCount(row("current.delv"));

  if (inns >= 2 && overs === 0 && part === 0 && delv === 0) {
    return !opts?.dismissZeroOverInningsBreak;
  }
  // Only innings 1 and 3 have a following innings in every format.
  if (inns !== 1 && inns !== 3) return false;
  if ((Number(row(`overs.${inns + 1}`)) || 0) > 0) return false;
  const max = Number(row("Max Overs")) || Number(row(`max.${inns}`)) || 0;
  const wktsBoard = Number(row(`wkts.${inns}`)) || 0;
  const cardInns = streamData?.scorecard?.[`inns${inns}`];
  const wktsCard = Number(cardInns?.wkts) || 0;
  const wkts = Math.max(wktsBoard, wktsCard);
  if ((max > 0 && overs >= max && part === 0) || wkts >= 10) return true;

  // CurPos / board often reset to 0.0 while still keyed to inns 1/3. Live overs then miss
  // `overs >= max` (e.g. 9 wickets after a full T20) — fall back to scorecard + scoregrid.
  if (overs === 0 && part === 0 && delv === 0) {
    if (opts?.dismissZeroOverInningsBreak) return false;
    const cardOvers = parseFloat(String(cardInns?.overs ?? "").trim());
    if (max > 0 && Number.isFinite(cardOvers) && cardOvers >= max) return true;
    if (max > 0 && cricketScoregridInnsMaxOver(streamData, inns) >= max) return true;
  }
  return false;
}

function cricketBreakInPlayLabelFromFeedText(raw: unknown): string {
  const text = String(raw ?? "").trim();
  if (!text) return "Break in Play";
  const withoutParen = text.replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim();
  if (/break in play/i.test(withoutParen)) {
    const parts = withoutParen.split(/\s*-\s*/);
    const tail = parts.slice(1).join(" - ").trim();
    return tail ? `Break in Play - ${tail}` : "Break in Play";
  }
  if (/rain delay/i.test(text) || cricketFeedTextIndicatesWeatherDelay(text)) {
    return "Break in Play - Rain Delay";
  }
  if (/\bdrinks\b/i.test(text)) return "Break in Play - Drinks";
  return "Break in Play";
}

function cricketBreakStatusFromFeedText(raw: unknown): MatchStatus {
  return {
    text: cricketBreakInPlayLabelFromFeedText(raw),
    color: CRICKET_BREAK_IN_PLAY_COLOR,
  };
}

/** Player-facing break line shared by the league card, Squares, Sixes, Bullion, and Trader. */
export function cricketPublicBreakLabel(raw: unknown): string {
  const text = cricketBreakInPlayLabelFromFeedText(raw);
  if (/rain delay/i.test(text) || cricketFeedTextIndicatesWeatherDelay(raw)) return "MATCH BREAK - RAIN DELAY";
  if (/drinks/i.test(text)) return "MATCH BREAK - DRINKS";
  if (/innings/i.test(text)) return "INNINGS BREAK";
  return "MATCH BREAK";
}

/**
 * Break label to show while Decimal is not calling a live delivery.
 * A ball in progress wins over a stale "Rain Delay" line. MATCH_STABLE does not:
 * the scoreboard keeps "Break in Play - Rain Delay" through status 7 and 1.
 */
export function cricketDisplayedBreakLabel(eventStatus: unknown, ...texts: unknown[]): string | null {
  if (cricketEventStatusIndicatesLiveDelivery(eventStatus)) return null;
  let specific: string | null = null;
  let generic = isBreakInPlayStatus(eventStatus);
  const consider = (raw: unknown) => {
    if (raw == null || raw === "") return;
    if (!isBreakInPlayStatus(raw) && !cricketFeedTextIndicatesBreakInPlay(raw)) return;
    const label = cricketPublicBreakLabel(raw);
    if (label === "MATCH BREAK") generic = true;
    else specific = specific ?? label;
  };
  for (const raw of texts) consider(raw);
  consider(eventStatus);
  if (specific) return specific;
  return generic ? "MATCH BREAK" : null;
}

/** "Break in Play" contains "in play" and must not read as the match being live. */
export function cricketPhaseSaysMatchInPlay(raw: unknown): boolean {
  const text = String(raw ?? "").trim();
  if (!text || cricketFeedTextIndicatesBreakInPlay(text)) return false;
  return text === "In Play" || text.includes("Match in Progress") || /\bin play\b/i.test(text);
}

/** True when internal list status text is a break (not ball-in-play). */
export function isCricketBreakInPlayMatchStatus(status: MatchStatus | { text: string }): boolean {
  const text = status.text.trim();
  return text === "Break in Play" || text.startsWith("Break in Play -");
}

/**
 * Break from fixture/list API fields when stream event_status has not caught up.
 */
export function getCricketBreakStatusFromMatch(match: Match): MatchStatus | null {
  const candidates = [match.status, (match as { event_status?: unknown }).event_status];
  for (const raw of candidates) {
    if (!cricketFeedTextIndicatesBreakInPlay(raw)) continue;
    return cricketBreakStatusFromFeedText(raw);
  }
  return null;
}

/**
 * Returns status to show for cricket when stream data is available (event_status, scorecard, scoreboard).
 * Use this so the list/card shows "Break in Play" when the feed says so, instead of generic "IN-PLAY".
 */
export const getCricketStatusFromStream = (
  streamData: any,
  opts?: CricketInningsBreakOpts,
): MatchStatus | null => {
  if (!streamData) return null;

  const status = streamData?.market_update?.event_status ?? streamData?.event?.event_status;
  const inningsBreak = cricketStreamIndicatesInningsBreak(streamData, opts);
  const underway = cricketStreamIndicatesNewInningsPlayUnderway(streamData);
  const skipStaleInningsRestartText =
    !inningsBreak &&
    (Boolean(opts?.dismissZeroOverInningsBreak) ||
      cricketEventStatusIndicatesLiveDelivery(status) ||
      underway);
  // Live event_status wins over a stale scorecard/game_phase break. Innings breaks still
  // apply: Decimal often sits on MATCH_STABLE with "Play to Restart" between innings.
  const skipStaleBreakCopy = !inningsBreak && cricketEventStatusIndicatesActivePlay(status);
  const overs = cricketScoreboardCount(cricketStreamScoreboardRow(streamData, "Current Innings Overs"));
  const part = cricketScoreboardCount(cricketStreamScoreboardRow(streamData, "Current Innings Part Overs"));
  const skipStaleBreakInPlayStatus =
    skipStaleInningsRestartText &&
    Boolean(opts?.dismissZeroOverInningsBreak) &&
    overs === 0 &&
    part === 0;

  let genericBreak: MatchStatus | null = null;
  for (const raw of cricketStreamBreakStatusSources(streamData)) {
    if (!cricketFeedTextIndicatesBreakInPlay(raw)) continue;
    if (skipStaleBreakCopy && raw !== status) continue;
    if (skipStaleBreakInPlayStatus && isBreakInPlayStatus(raw)) continue;
    if (
      skipStaleInningsRestartText &&
      cricketFeedTextIndicatesInningsRestartGap(raw) &&
      !cricketFeedTextIndicatesWeatherDelay(raw) &&
      !/\bDRINKS\b/i.test(String(raw))
    ) {
      continue;
    }
    const next = cricketBreakStatusFromFeedText(raw);
    // Status 7 is only "Break in Play". Keep looking for the rain / drinks reason on game_phase.
    if (next.text === "Break in Play") {
      genericBreak = genericBreak ?? next;
      continue;
    }
    return next;
  }
  if (genericBreak) return genericBreak;

  if (inningsBreak) {
    return { text: "Break in Play - Innings Break", color: CRICKET_BREAK_IN_PLAY_COLOR };
  }

  return null;
};

/**
 * Cricket match cards on /matches and Trader: merge stream + fixture status.
 * Break must win over the generic stream IN-PLAY fallback (scorecard alone is not enough).
 * When a stream snapshot exists and reports no break, do not keep a stale list
 * "Toss Delayed / Rain Delay" label (same bug the league hub card had).
 */
export function resolveCricketMatchListStatus(
  match: Match,
  streamData: any | undefined,
  fromGet: MatchStatus,
): MatchStatus {
  const prematchCountdown =
    fromGet.text.startsWith("STARTS IN ") || /^in\s+/i.test(fromGet.text);
  if (prematchCountdown) return fromGet;

  if (streamData) {
    const breakFromStream = getCricketStatusFromStream(streamData);
    if (breakFromStream) return breakFromStream;
    if (streamDataIndicatesCricketInPlay(streamData)) {
      return { text: "IN-PLAY", color: "text-pink" };
    }
    // Stream is attached but not on a break — ignore list-only rain/break status.
    if (isCricketBreakInPlayMatchStatus(fromGet) || getCricketBreakStatusFromMatch(match)) {
      return { text: "SCHEDULED", color: "text-white" };
    }
    return fromGet;
  }

  const breakFromMatch = getCricketBreakStatusFromMatch(match);
  if (breakFromMatch) return breakFromMatch;

  if (isCricketBreakInPlayMatchStatus(fromGet)) return fromGet;

  return fromGet;
}

/**
 * Format countdown string from time difference
 */
export const formatCountdown = (startTime: number, now: number): string | null => {
  const duration = formatCountdownDuration(startTime - now);
  return duration ? `STARTS IN ${duration}` : null;
};

/**
 * Get match status (IN-PLAY, in Xm, STARTS IN …, etc.)
 */
export const getMatchStatus = (match: Match): MatchStatus => {
  const status = (match.status || "").toUpperCase();
  const sport = (match.sport || "").toLowerCase();
  
  // Check if match is finished/completed
  // For horse races, also check for EVENT_FULL_RESULT status
  const isFinished = 
    status.includes("MATCH COMPLETE") ||
    status.includes("FINISHED") ||
    status.includes("FULL TIME") ||
    status.includes("EVENT CLOSED") ||
    status === "EVENT_FULL_RESULT" ||
    match.liveScore?.isFinished === true;
  
  // For horse races, also check if there are results (horses with positions)
  // This helps detect finished races even if status isn't explicitly set
  if (sport === "horses" && !isFinished) {
    const goalserveData = (match as any).goalserveData;
    if (goalserveData?.horses && Array.isArray(goalserveData.horses)) {
      const hasResults = goalserveData.horses.some((horse: any) => 
        horse.position && horse.position > 0
      );
      if (hasResults) {
        return { text: "FINISHED", color: "text-white" };
      }
    }
  }
  
  // Return FINISHED status if match is finished
  if (isFinished) {
    return { text: "FINISHED", color: "text-white" };
  }

  // Weather / toss delay must beat the kickoff-passed "IN-PLAY" fallback.
  if (sport === "cricket" && cricketFeedTextIndicatesBreakInPlay(match.status)) {
    return cricketBreakStatusFromFeedText(match.status);
  }
  
  // Prematch Goalserve @status is often HH:MM kick-off clock (UTC), not a live minute.
  const liveScoreStatus = (match.liveScore?.status || "").trim();
  const isKickoffClockStatus = /^\d{1,2}:\d{2}$/.test(status) || /^\d{1,2}:\d{2}$/.test(liveScoreStatus);

  // Feed/live-score signals — ignored for display when kickoff is still in the future
  const looksLikeFootballLiveMinute =
    !isKickoffClockStatus &&
    (/^\d+(\+\d+)?'?$/.test(status) ||
      status.includes("'") ||
      (status.includes("+") && /^\d/.test(status)) ||
      /^\d+(\+\d+)?'?$/.test(liveScoreStatus));

  const isLiveFromFeed = 
    status.includes("MATCH IN PROGRESS") ||
    status.includes("IN-PLAY") ||
    status.includes("LIVE") ||
    status.includes("BREAK IN PLAY") || // Cricket: "Break in Play - Stumps", "Break in Play - Drinks", etc.
    status.includes("INNINGS COMPLETE") || // Cricket: Between innings, match still ongoing
    status.includes("PLAY TO RESTART") || // Cricket: after a break, before play resumes
    /\bPLAY TO \d/.test(status) || // Cricket: "Play to 150" chase target (not "Play to Start Shortly")
    looksLikeFootballLiveMinute ||
    (!isKickoffClockStatus && match.liveScore?.isLive === true); // Tennis/Football live score indicator
  
  // Backend always sets actual_start for horses (same as start when not postponed). Use only this for status/countdown.
  const effectiveStart = (match as any).actual_start ?? match.start;
  const startTime = effectiveStart ? new Date(effectiveStart).getTime() : NaN;
  const now = Date.now();
  const minutesUntilStart = Number.isFinite(startTime) ? (startTime - now) / (1000 * 60) : null;

  // Kickoff still in the future: never show IN PLAY — prefer imminent "in Xm" or STARTS IN
  if (minutesUntilStart != null && minutesUntilStart > 0) {
    if (minutesUntilStart <= MATCH_IMMINENT_MINUTES) {
      const imminent = formatImminentCountdown(startTime - now);
      if (imminent) {
        return { text: imminent, color: "text-[#32F78D]" };
      }
    }
    const countdown = formatCountdown(startTime, now);
    if (countdown) {
      return { text: countdown, color: "text-[#32F78D]" };
    }
  }

  // If match has started (start time passed) and not finished, consider it IN-PLAY
  if (effectiveStart && !isFinished && !isLiveFromFeed && Number.isFinite(startTime)) {
    const minutesSinceStart = (now - startTime) / (1000 * 60);
    
    if (sport === "horses") {
      // Horse race: IN-PLAY if started and less than 30 minutes ago (reasonable max for a race)
      if (minutesSinceStart >= 0 && minutesSinceStart < 30) {
        return { text: "IN-PLAY", color: "text-pink" };
      }
      // If horse race started more than 30 minutes ago and we can't determine if it's finished,
      // mark it as FINISHED (races typically last only a few minutes)
      if (minutesSinceStart >= 30) {
        return { text: "FINISHED", color: "text-white" };
      }
    } else if (sport === "cricket") {
      // Cricket: kickoff-passed is a weak signal. Weather / toss delay already returned above.
      // Skip IN-PLAY while the list status is still explicitly scheduled / pretoss (Decimal often
      // keeps those labels with curBall -3 until the first ball). Empty status still falls through
      // to IN-PLAY so list cards stay live when the feed is sparse.
      const pretossLike =
        status.includes("SCHEDULED") ||
        status.includes("STARTING SOON") ||
        status.includes("TOSS PENDING") ||
        status.includes("AWAITING TOSS") ||
        status.includes("PRE MATCH") ||
        status.includes("PRE-MATCH");
      if (!pretossLike && minutesSinceStart >= 0 && minutesSinceStart < 720) {
        return { text: "IN-PLAY", color: "text-pink" };
      }
    } else {
      // Other sports: If match started more than 5 minutes ago and less than 4 hours ago, consider it IN-PLAY
      // (4 hours is a reasonable max match duration)
      if (minutesSinceStart > 5 && minutesSinceStart < 240) {
        return { text: "IN-PLAY", color: "text-pink" };
      }
    }
  }
  
  // Cricket: show "Break in Play" when feed says so (same as match-play), not generic IN-PLAY
  if (sport === "cricket" && (status.includes("BREAK IN PLAY") || status.includes("PLAY TO RESTART"))) {
    return { text: "Break in Play", color: "text-[#a855f7]" };
  }
  if (isLiveFromFeed) {
    return { text: "IN-PLAY", color: "text-pink" };
  }
  
  // Fallback for matches without start time
  if (status.includes("STARTING SOON") || status.includes("SCHEDULED")) {
    return { text: "STARTING SOON", color: "text-purple" };
  }
  return { text: "", color: "text-white" };
};

/**
 * Parse cricket match data from match.name
 */
export const parseCricketMatchName = (name: string) => {
  if (!name) return { teams: [], competition: "" };

  const parts = name.split(",");
  if (parts.length >= 2) {
    const teams = parts[0].split(" v ").map(t => t.trim()).filter(t => t);
    return {
      teams,
      competition: parts[1]?.trim() || parts[2]?.trim() || "",
    };
  }
  const teams = name.split(" v ").map(t => t.trim()).filter(t => t);
  return { teams, competition: "" };
};

/**
 * Extract venue name from horse race name by removing time prefix (HH:MM format)
 * Handles formats like:
 * - "16:00 Wolverhampton" -> "Wolverhampton"
 * - "20:24 Sam Houston Race Park" -> "Sam Houston Race Park"
 * - "1 - Aqueduct Race 1" -> "1 - Aqueduct Race 1" (no time prefix, return as is)
 */
export const extractHorseVenueName = (raceName: string): string => {
  if (!raceName) return "";
  
  // Remove time prefix in HH:MM or H:MM format at the start
  // Pattern matches: "16:00 " or "8:00 " at the beginning
  const timePrefixPattern = /^\d{1,2}:\d{2}\s+/;
  const cleaned = raceName.replace(timePrefixPattern, "").trim();
  
  return cleaned || raceName;
};

/**
 * Parse football/tennis match data from event-participants and meta-tags
 */
export const parseFootballTennisMatchData = (match: Match) => {
  if (!match) return { teams: [], competition: "" };
  
  const participants = match["event-participants"] || [];
  const teams = participants
    .map(p => p?.["participant-name"] || p?.name)
    .filter(name => name && name.trim() !== "");

  // Get competition from meta-tags
  const metaTags = match["meta-tags"] || [];
  let competition = "";
  for (const tag of metaTags) {
    if (tag?.type?.toString().toUpperCase() === "COMPETITION") {
      competition = tag?.name || "";
      break;
    }
  }
  if (!competition) {
    for (const tag of metaTags) {
      if (tag?.type?.toString().toUpperCase() === "COUNTRY" && (tag?.name || "").trim().toLowerCase() === "world") {
        competition = "FIFA World Cup";
        break;
      }
    }
  }

  return { teams, competition };
};

/**
 */
export const extractMatchWinnerOdds = (match: Match): Match["event-participants"] => {
  const participants = match["event-participants"] || [];
  if (!match.markets || match.markets.length === 0) {
    return participants;
  }

  const sport = (match.sport || "").toLowerCase();
  const matchOddsMarket = findMarketBySport(match, sport);
  if (!matchOddsMarket?.runners?.length) {
    return participants;
  }

  return participants.map((participant) => {
    const matchingRunner = findMatchingRunner(matchOddsMarket.runners, participant);
    if (!matchingRunner) {
      return participant;
    }
    const odds = getOddsFromRunner(matchingRunner);
    const resolvedOdds = odds != null && odds > 0 ? odds : (participant.odds ?? participant.price);
    return {
      ...participant,
      odds: resolvedOdds ?? participant.odds,
      price: resolvedOdds ?? participant.price,
    };
  });
};

/** True when liveScore has football goals we can show on a card. */
export const hasFootballLiveScoreValues = (liveScore: Record<string, unknown> | null | undefined): boolean => {
  if (!liveScore) return false;
  const home = liveScore.home;
  const away = liveScore.away;
  return home != null && away != null && home !== "?" && away !== "?";
};

/** Whether to render football/tennis live scores on lobby/matches cards. */
export const shouldShowSportLiveScore = (
  liveScore: Record<string, unknown> | null | undefined,
  isInPlay: boolean
): boolean => {
  if (!liveScore) return false;
  if (liveScore.isLive === true || liveScore.isFinished === true) return true;
  if (isInPlay && hasFootballLiveScoreValues(liveScore)) return true;
  if (isInPlay && (liveScore.player1Sets || liveScore.player2Sets)) return true;
  if (hasFootballLiveScoreValues(liveScore)) return true;
  if (liveScore.player1Sets || liveScore.player2Sets) return true;
  return false;
};
